import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useBluelagoon } from "@bluelagoon";
import { Copy, Package, RefreshCw, Loader2, ChevronDown, CheckCircle2, Link2 } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@shadcn-in-nx/ui/dialog";
import { Input } from "@shadcn-in-nx/ui/input";
import { Label } from "@shadcn-in-nx/ui/label";
import { Button } from "@shadcn-in-nx/ui/button";
import ShareUrlCopyField from "../../../Chat/components/ShareUrlCopyField";
import CopyWorkflowOption from "./CopyWorkflowOption";
import useCopyWorkflow from "../hooks/useCopyWorkflow";

const DEFAULT_MODE = "maintainable";

function buildShareUrl(token) {
    if (!token || typeof window === "undefined") return null;
    return `${window.location.origin}/blueprints/share/${token}`;
}

export default function CopyWorkflowModal({ open, onOpenChange, workflow, onCopied }) {
    const { t } = useTranslation();
    const [mode, setMode] = useState(DEFAULT_MODE);
    const [name, setName] = useState("");
    const [showHelp, setShowHelp] = useState(false);
    const [step, setStep] = useState("select");
    const [shareToken, setShareToken] = useState(null);
    const [existing, setExisting] = useState(null);
    const [pendingCopied, setPendingCopied] = useState(null);
    const { submit, isSubmitting } = useCopyWorkflow();
    const { call: fetchExisting } = useBluelagoon({ method: "getMaintainableBlueprintByWorkflow" });

    useEffect(() => {
        if (!open || !workflow) return;
        setMode(DEFAULT_MODE);
        setShowHelp(false);
        setStep("select");
        setShareToken(null);
        setExisting(null);
        setPendingCopied(null);
        setName(`${workflow.name} Blueprint`);
        fetchExisting({ workflowId: workflow.id }).then((res) => {
            const data = res?.data?.data || res?.data;
            if (data?.shareToken) setExisting(data);
        });
    }, [open, workflow, fetchExisting]);

    useEffect(() => {
        if (!workflow || step !== "select") return;
        if (mode === "duplicate") setName(`${workflow.name} (Copy)`);
        else setName(`${workflow.name} Blueprint`);
    }, [mode, workflow, step]);

    const handleSubmit = async () => {
        const data = await submit({ workflow, mode, name });
        if (!data) return;
        if (mode === "maintainable" && data.shareToken) {
            setShareToken(data.shareToken);
            setStep("shared");
            setPendingCopied({ mode, data });
            return;
        }
        onCopied?.({ mode, data });
        onOpenChange(false);
    };

    const handleClose = () => {
        if (pendingCopied) {
            const payload = pendingCopied;
            setPendingCopied(null);
            onOpenChange(false);
            onCopied?.(payload);
            return;
        }
        onOpenChange(false);
    };

    const cta =
        mode === "duplicate"
            ? t("workflowIndex.copy.cta.duplicate")
            : mode === "maintainable"
              ? t("workflowIndex.copy.cta.maintainable")
              : t("workflowIndex.copy.cta.blueprint");

    const shareUrl = buildShareUrl(shareToken);
    const existingShareUrl = buildShareUrl(existing?.shareToken);
    const maintainableExists = mode === "maintainable" && !!existing;
    const ctaDisabled = isSubmitting || !name.trim() || maintainableExists;

    return (
        <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : handleClose())}>
            <DialogContent className="sm:max-w-lg">
                {step === "shared" ? (
                    <>
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <CheckCircle2 className="w-5 h-5 text-green-600" />
                                {t("workflowIndex.copy.shared.title")}
                            </DialogTitle>
                            <DialogDescription>
                                {t("workflowIndex.copy.shared.description")}
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-3">
                            <Label className="text-xs">
                                {t("workflowIndex.copy.shared.urlLabel")}
                            </Label>
                            {shareUrl && <ShareUrlCopyField url={shareUrl} />}
                            <div className="text-xs text-gray-600 bg-blue-50/60 border border-blue-100 rounded-md p-3 leading-relaxed">
                                {t("workflowIndex.copy.shared.tip")}
                            </div>
                        </div>
                        <DialogFooter>
                            <Button onClick={handleClose}>
                                {t("workflowIndex.copy.shared.done")}
                            </Button>
                        </DialogFooter>
                    </>
                ) : (
                    <>
                        <DialogHeader>
                            <DialogTitle>{t("workflowIndex.copy.title")}</DialogTitle>
                            <DialogDescription>
                                {t("workflowIndex.copy.description", { name: workflow?.name || "" })}
                            </DialogDescription>
                        </DialogHeader>

                        <div className="space-y-2">
                            <CopyWorkflowOption
                                autoFocus
                                icon={RefreshCw}
                                iconBgClass="bg-blue-100"
                                iconColorClass="text-blue-700"
                                label={t("workflowIndex.copy.options.maintainable.label")}
                                description={t("workflowIndex.copy.options.maintainable.description")}
                                badge={t("workflowIndex.copy.options.recommended")}
                                selected={mode === "maintainable"}
                                onClick={() => setMode("maintainable")}
                            />
                            {mode === "maintainable" && (
                                <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-3 space-y-2 ml-2">
                                    {existing && existingShareUrl ? (
                                        <div className="space-y-2">
                                            <div className="flex items-center gap-1.5 text-xs font-medium text-blue-800">
                                                <Link2 className="w-3.5 h-3.5" />
                                                {t("workflowIndex.copy.existing.title")}
                                            </div>
                                            <ShareUrlCopyField url={existingShareUrl} />
                                            <p className="text-[11px] text-gray-600">
                                                {t("workflowIndex.copy.existing.note")}
                                            </p>
                                        </div>
                                    ) : (
                                        <>
                                            <button
                                                type="button"
                                                onClick={() => setShowHelp((s) => !s)}
                                                className="flex items-center gap-1 text-xs font-medium text-blue-700 hover:text-blue-800"
                                            >
                                                <ChevronDown className={`w-3 h-3 transition-transform ${showHelp ? "rotate-0" : "-rotate-90"}`} />
                                                {t("createBlueprint.maintainableHelp.title")}
                                            </button>
                                            {showHelp && (
                                                <div className="text-xs text-gray-700 space-y-2 pl-4">
                                                    <p>{t("createBlueprint.maintainableHelp.intro")}</p>
                                                    <ol className="list-decimal list-inside space-y-1">
                                                        <li>{t("createBlueprint.maintainableHelp.step1")}</li>
                                                        <li>{t("createBlueprint.maintainableHelp.step2")}</li>
                                                        <li>{t("createBlueprint.maintainableHelp.step3")}</li>
                                                        <li>{t("createBlueprint.maintainableHelp.step4")}</li>
                                                    </ol>
                                                    <p className="text-[11px] text-gray-500 pt-1 border-t border-blue-100">
                                                        {t("createBlueprint.maintainableHelp.note")}
                                                    </p>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                            <CopyWorkflowOption
                                icon={Copy}
                                iconBgClass="bg-emerald-100"
                                iconColorClass="text-emerald-700"
                                label={t("workflowIndex.copy.options.duplicate.label")}
                                description={t("workflowIndex.copy.options.duplicate.description")}
                                selected={mode === "duplicate"}
                                onClick={() => setMode("duplicate")}
                            />
                            <CopyWorkflowOption
                                icon={Package}
                                iconBgClass="bg-violet-100"
                                iconColorClass="text-violet-700"
                                label={t("workflowIndex.copy.options.blueprint.label")}
                                description={t("workflowIndex.copy.options.blueprint.description")}
                                selected={mode === "blueprint"}
                                onClick={() => setMode("blueprint")}
                            />
                        </div>

                        {!maintainableExists && (
                            <div className="space-y-2">
                                <Label htmlFor="copy-name" className="text-xs">
                                    {t("workflowIndex.copy.nameLabel")}
                                </Label>
                                <Input
                                    id="copy-name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder={t("workflowIndex.copy.namePlaceholder")}
                                />
                            </div>
                        )}

                        <DialogFooter className="gap-2">
                            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
                                {t("workflowIndex.copy.cancel")}
                            </Button>
                            {!maintainableExists && (
                                <Button onClick={handleSubmit} disabled={ctaDisabled}>
                                    {isSubmitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                                    {cta}
                                </Button>
                            )}
                        </DialogFooter>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
